import { useCallback, useEffect, useRef, useState } from 'react';

const KEY = 'practcom.micId';
const SOUND = 14; // peak deviation (0-128) that counts as hearing a voice
const TEST_MS = 8000;

export const micErrorText = (e) => {
  const n = e?.name;
  if (n === 'NotAllowedError' || n === 'SecurityError') return 'Microphone is blocked. Click the lock icon in your browser address bar, set Microphone to Allow, then try again.';
  if (n === 'NotFoundError' || n === 'OverconstrainedError') return 'No microphone found. Plug in or turn on a microphone, then try again.';
  if (n === 'NotReadableError') return 'Your microphone is being used by another app (Zoom, Teams, etc.). Close that app and try again.';
  return 'Microphone not available. Check your microphone or switch to typing.';
};

const openStream = async (micId) => {
  const base = { echoCancellation: true, noiseSuppression: true };
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: micId ? { ...base, deviceId: { exact: micId } } : base });
  } catch (e) {
    if (!micId || !['OverconstrainedError', 'NotFoundError'].includes(e.name)) throw e;
    localStorage.removeItem(KEY);
    return navigator.mediaDevices.getUserMedia({ audio: base });
  }
};

const useMicDevices = () => {
  const [devices, setDevices] = useState([]);
  const [micId, setMicIdState] = useState(() => localStorage.getItem(KEY) || '');
  const refresh = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter((d) => d.kind === 'audioinput' && d.deviceId && !['default', 'communications'].includes(d.deviceId)));
    } catch { setDevices([]); }
  }, []);
  useEffect(() => {
    refresh();
    const md = navigator.mediaDevices;
    md?.addEventListener?.('devicechange', refresh);
    return () => md?.removeEventListener?.('devicechange', refresh);
  }, [refresh]);
  const setMicId = (id) => { if (id) localStorage.setItem(KEY, id); else localStorage.removeItem(KEY); setMicIdState(id); };
  return { devices, micId, setMicId, refresh };
};

// Opens the mic with a live level meter. mode 'record' captures audio; mode 'test' only checks the mic.
// Silent recordings are never passed to onBlob.
export const useMicRecorder = ({ onBlob, autoStop = false, warnAfterMs = 4000 }) => {
  const dev = useMicDevices();
  const [mode, setMode] = useState(null);
  const [level, setLevel] = useState(0);
  const [heard, setHeard] = useState(false);
  const [silent, setSilent] = useState(false);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [testResult, setTestResult] = useState('');
  const ref = useRef({});
  const onBlobRef = useRef(onBlob);
  onBlobRef.current = onBlob;

  const cleanup = () => {
    const r = ref.current;
    cancelAnimationFrame(r.raf);
    r.stream?.getTracks().forEach((t) => t.stop());
    r.ctx?.close().catch(() => {});
    ref.current = {};
    setMode(null);
    setLevel(0);
    setSilent(false);
  };
  useEffect(() => () => cleanup(), []);

  const finish = () => {
    const r = ref.current;
    if (!r.stream || r.finishing) return;
    r.finishing = true;
    if (r.rec?.state === 'recording') { r.rec.stop(); return; }
    if (r.mode === 'test') setTestResult(r.heard ? 'ok' : 'silent');
    cleanup();
  };

  const start = async (m = 'record') => {
    if (ref.current.stream) cleanup();
    setError(''); setTestResult(''); setHeard(false); setSilent(false);
    let stream;
    try {
      stream = await openStream(dev.micId);
    } catch (e) {
      setError(micErrorText(e));
      return;
    }
    dev.refresh();
    const track = stream.getAudioTracks()[0];
    setLabel(track?.label || 'Default microphone');
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctx.resume?.().catch(() => {});
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(an);
    const buf = new Uint8Array(an.fftSize);
    const now = Date.now();
    ref.current = { stream, ctx, mode: m, heard: false, quietSince: now, startedAt: now, lastPaint: 0 };
    track?.addEventListener('ended', () => { setError('The microphone was disconnected. Plug it back in and try again.'); finish(); });

    if (m === 'record') {
      const type = ['audio/webm', 'audio/mp4', 'audio/ogg'].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || '';
      const rec = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        const wasHeard = ref.current.heard;
        cleanup();
        if (wasHeard) onBlobRef.current?.(new Blob(chunks, { type: rec.mimeType || 'audio/webm' }));
        else setError("We didn't hear anything, so nothing was sent. Check that the right microphone is selected and not muted, use Test mic, then try again.");
      };
      ref.current.rec = rec;
      rec.start();
    }
    setMode(m);

    const tick = () => {
      const r = ref.current;
      if (!r.stream || r.finishing) return;
      an.getByteTimeDomainData(buf);
      let vol = 0;
      for (let i = 0; i < buf.length; i += 1) vol = Math.max(vol, Math.abs(buf[i] - 128));
      const t = Date.now();
      if (vol > SOUND) {
        if (!r.heard) setHeard(true);
        r.heard = true;
        r.quietSince = t;
      }
      const quiet = t - r.quietSince;
      if (t - r.lastPaint > 80) {
        r.lastPaint = t;
        setLevel(Math.min(1, vol / 40));
        setSilent(quiet > warnAfterMs);
      }
      const elapsed = t - r.startedAt;
      if (r.mode === 'test' && (elapsed > TEST_MS || (r.heard && elapsed > 2500))) { finish(); return; }
      if (autoStop && ((r.heard && quiet > 1600) || (!r.heard && quiet > 8000) || elapsed > 60000)) { finish(); return; }
      r.raf = requestAnimationFrame(tick);
    };
    tick();
  };

  const known = dev.devices.find((d) => d.deviceId === dev.micId) || dev.devices[0];
  return {
    ...dev, start, label: label || known?.label || '', stop: finish, test: () => start('test'),
    recording: mode === 'record', testing: mode === 'test', active: !!mode,
    level, heard, silent, error, testResult, clearError: () => setError(''),
  };
};

export default useMicRecorder;
